import React, { createContext, useContext, useEffect, useState } from 'react';
import { collection, doc, onSnapshot, query, Timestamp, where } from 'firebase/firestore';
import { setDoc } from '../config/escrituras';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { usePerfil } from './PerfilContext';
import { SincronizacionPlan } from '../types';

/**
 * Qué planes de A medias pasa cada usuario a sus Finanzas, y cómo.
 *
 * Es el puente entre los dos módulos, así que va en su propio contexto en vez
 * de engordar DataContext o AMediasContext. La colección es de Finanzas: sin
 * ese módulo las reglas deniegan la lectura y simplemente no hay ninguna.
 */
type DatosSincronizacion = Pick<SincronizacionPlan, 'activa' | 'cuentaId' | 'categoriaId' | 'automatica'>;

interface SincronizacionAMediasContextType {
  sincronizaciones: SincronizacionPlan[];
  sincronizacionesCargadas: boolean;
  sincronizacionDe: (planId: string) => SincronizacionPlan | undefined;
  guardarSincronizacion: (planId: string, datos: DatosSincronizacion) => Promise<void>;
}

const Ctx = createContext<SincronizacionAMediasContextType | undefined>(undefined);

export const SincronizacionAMediasProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  // Solo se escucha con el módulo activado: sin él, las reglas rechazan la
  // consulta y el listener muere; al activarlo, el efecto vuelve a suscribirse.
  const { tieneModulo } = usePerfil();
  const conModulo = tieneModulo('finanzas');
  const [sincronizaciones, setSincronizaciones] = useState<SincronizacionPlan[]>([]);
  const [cargadas, setCargadas] = useState(false);

  useEffect(() => {
    setSincronizaciones([]);
    setCargadas(false);
    if (!user || !conModulo) { setCargadas(true); return; }
    const q = query(collection(db, 'sincronizacionesPlan'), where('usuarioId', '==', user.uid));
    return onSnapshot(q, snap => {
      setSincronizaciones(snap.docs.map(d => {
        const x = d.data();
        return {
          id: d.id,
          usuarioId: x.usuarioId,
          planId: x.planId,
          activa: x.activa === true,
          cuentaId: x.cuentaId ?? '',
          categoriaId: x.categoriaId ?? '',
          automatica: x.automatica === true,
          createdAt: x.createdAt?.toDate() ?? new Date(),
        };
      }));
      setCargadas(true);
    }, err => {
      // Sin Finanzas no hay sincronización posible: no es un fallo
      console.warn('Sincronización con Finanzas no disponible:', err.code);
      setCargadas(true);
    });
  }, [user, conModulo]);

  const sincronizacionDe = (planId: string) => sincronizaciones.find(s => s.planId === planId);

  // Un documento por usuario y plan, con id fijo: así no puede haber dos
  const guardarSincronizacion = async (planId: string, datos: DatosSincronizacion) => {
    if (!user) throw new Error('Sesión no iniciada');
    const existente = sincronizacionDe(planId);
    await setDoc(doc(db, 'sincronizacionesPlan', `${user.uid}_${planId}`), {
      ...datos,
      usuarioId: user.uid,
      planId,
      createdAt: existente ? Timestamp.fromDate(existente.createdAt) : Timestamp.now(),
    });
  };

  return (
    <Ctx.Provider value={{ sincronizaciones, sincronizacionesCargadas: cargadas, sincronizacionDe, guardarSincronizacion }}>
      {children}
    </Ctx.Provider>
  );
};

export const useSincronizacionAMedias = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSincronizacionAMedias debe usarse dentro de SincronizacionAMediasProvider');
  return ctx;
};
